/**
 * Textos interpretativos exclusivos del PDF ejecutivo NOM-035.
 * No altera umbrales ni cálculos de riesgo; solo redacción orientativa.
 */

import type { Nom35RiskLevel } from '@/lib/nom35';
import type {
  Nom35ExecutiveDemographicCrosses,
  Nom35ExecutiveGlobalResult,
} from '@/lib/nom35Executive';

export type DomainRow = { name: string; score: number; risk: Nom35RiskLevel };

export type CategorySection = {
  title: string;
  intro: string;
  domains: string[];
  onlyG3?: boolean;
};

export const NOM35_PDF_CATEGORY_SECTIONS: CategorySection[] = [
  {
    title: 'Categoría: Ambiente de Trabajo',
    intro: 'Evalúa las condiciones físicas, peligrosas o insalubres del lugar de trabajo.',
    domains: ['Condiciones en el ambiente de trabajo'],
  },
  {
    title: 'Categoría: Factores propios de la actividad',
    intro: 'Analiza las exigencias de tiempo, esfuerzo y ritmo de trabajo, así como la autonomía del colaborador.',
    domains: ['Carga de trabajo', 'Falta de control sobre el trabajo', 'Responsabilidades'],
  },
  {
    title: 'Categoría: Organización del tiempo de trabajo',
    intro: 'Mide la interferencia en la relación trabajo-familia y la jornada laboral.',
    domains: ['Jornada de trabajo', 'Interferencia en la relación trabajo-familia'],
  },
  {
    title: 'Categoría: Liderazgo y relaciones en el trabajo',
    intro: 'Considera el apoyo social, la calidad de la interacción y la prevención de violencia laboral.',
    domains: ['Liderazgo', 'Relaciones en el trabajo', 'Violencia'],
  },
  {
    title: 'Categoría: Entorno organizacional (Solo G3)',
    intro: 'Mide el sentido de pertenencia y el reconocimiento al desempeño.',
    domains: ['Reconocimiento del desempeño', 'Insuficiente sentido de pertenencia e inestabilidad'],
    onlyG3: true,
  },
];

/** Misma lógica relativa dentro del informe PDF que antes vivía en el componente (sin cambiar reglas). */
export function nom35PdfScoreToRisk(score: number, values: number[]): Nom35RiskLevel {
  if (values.length === 0) return 'Nulo';
  const max = Math.max(...values);
  if (max <= 0) return 'Nulo';
  const pct = score / max;
  if (pct <= 0.2) return 'Nulo';
  if (pct <= 0.4) return 'Bajo';
  if (pct <= 0.6) return 'Medio';
  if (pct <= 0.8) return 'Alto';
  return 'Muy Alto';
}

export function buildOfficialCategoryRows(
  data: NonNullable<Nom35ExecutiveGlobalResult['g2g3']>
): { category: CategorySection; rows: DomainRow[] }[] {
  const source = data.globalAverages.averageByCategory;
  const normalizedMap: Record<string, number> = {};

  for (const [rawDomain, value] of Object.entries(source)) {
    const key = rawDomain.toLowerCase();
    let officialDomain = rawDomain;
    if (key.includes('ambiente') || key.includes('entorno')) officialDomain = 'Condiciones en el ambiente de trabajo';
    else if (key.includes('carga')) officialDomain = 'Carga de trabajo';
    else if (key.includes('control') || key.includes('decisiones')) officialDomain = 'Falta de control sobre el trabajo';
    else if (key.includes('jornada')) officialDomain = 'Jornada de trabajo';
    else if (key.includes('familia') || key.includes('tiempo') || key.includes('responsabilidades')) {
      officialDomain = 'Interferencia en la relación trabajo-familia';
    } else if (key.includes('liderazgo') || key.includes('jefatura')) officialDomain = 'Liderazgo';
    else if (key.includes('relaciones')) officialDomain = 'Relaciones en el trabajo';
    else if (key.includes('violencia')) officialDomain = 'Violencia';
    else if (key.includes('reconocimiento') || key.includes('pertenencia')) officialDomain = 'Reconocimiento del desempeño';
    else if (key.includes('cambios') || key.includes('capacitación') || key.includes('inestabilidad')) {
      officialDomain = 'Insuficiente sentido de pertenencia e inestabilidad';
    }

    normalizedMap[officialDomain] = (normalizedMap[officialDomain] ?? 0) + (value ?? 0);
  }

  const allScores: number[] = Object.values(normalizedMap);
  const visibleCategories = NOM35_PDF_CATEGORY_SECTIONS.filter((c) => !(c.onlyG3 && data.guideType !== 'NOM35_G3'));

  return visibleCategories.map((category) => {
    const rows = category.domains.map((name) => {
      const score = normalizedMap[name] ?? 0;
      return { name, score, risk: nom35PdfScoreToRisk(score, allScores) };
    });
    return { category, rows };
  });
}

const RISK_ORDER: Nom35RiskLevel[] = ['Nulo', 'Bajo', 'Medio', 'Alto', 'Muy Alto'];

function topRiskFromDomains(rows: DomainRow[]): Nom35RiskLevel {
  return rows.reduce<Nom35RiskLevel>(
    (acc, item) => (RISK_ORDER.indexOf(item.risk) > RISK_ORDER.indexOf(acc) ? item.risk : acc),
    'Nulo'
  );
}

function isElevated(r: Nom35RiskLevel): boolean {
  return r === 'Medio' || r === 'Alto' || r === 'Muy Alto';
}

function riskUrgencyLabel(globalRisk: Nom35RiskLevel): string {
  if (globalRisk === 'Muy Alto') return 'urgencia alta: conviene priorizar decisiones y recursos en las próximas semanas.';
  if (globalRisk === 'Alto') return 'urgencia moderada-alta: se recomienda calendarizar intervenciones estructuradas sin demora.';
  if (globalRisk === 'Medio') return 'urgencia moderada: abordaje planificado y seguimiento cercano suelen ser suficientes si se actúa con constancia.';
  if (globalRisk === 'Bajo') return 'urgencia contenida: mantener prevención y revisión periódica.';
  return 'urgencia baja en términos globales: conservar buenas prácticas y monitoreo.';
}

/** Tendencias grupales G2/G3 — sin diagnósticos individuales. */
export function analyzeRiskDemographicExecutive(
  table: Nom35ExecutiveDemographicCrosses['riskBySex'],
  demographicName: string
): string {
  let maxPct = -1;
  let maxKey = '';
  for (const [key, counts] of Object.entries(table)) {
    if (counts.total === 0 || key === 'No especificado') continue;
    const highRiskCount = counts.alto + counts.muyAlto;
    const pct = highRiskCount / counts.total;
    if (pct > maxPct && highRiskCount > 0) {
      maxPct = pct;
      maxKey = key;
    }
  }
  if (maxPct === -1) {
    return `Lectura grupal: al agrupar por ${demographicName}, no se observa una concentración destacable en niveles Alto o Muy Alto. El reparto del riesgo en este corte es relativamente parejo; conviene seguir monitoreando y, si hay alertas operativas, complementar con escucha cualitativa (entrevistas o grupos focales).`;
  }
  return `Lectura grupal (tendencias agregadas, no casos individuales): en el corte por ${demographicName}, el grupo "${maxKey}" concentra la mayor proporción de personas en riesgo Alto o Muy Alto (${(maxPct * 100).toFixed(1)}% dentro de ese grupo). Esto puede orientar acciones focalizadas (por ejemplo, diálogo con líderes del área o revisión de cargas y procesos), sin inferir causas ni atribuir responsabilidades personales. Conviene contrastar con información cualitativa antes de decisiones relevantes.`;
}

/** Guía I — lenguaje preventivo y grupal. */
export function analyzeG1DemographicExecutive(
  table: Record<string, { requiereAtencion: number; noRequiere: number; total: number }>,
  demographicName: string
): string {
  let maxPct = -1;
  let maxKey = '';
  for (const [key, counts] of Object.entries(table)) {
    if (counts.total === 0 || key === 'No especificado') continue;
    const pct = counts.requiereAtencion / counts.total;
    if (pct > maxPct && counts.requiereAtencion > 0) {
      maxPct = pct;
      maxKey = key;
    }
  }
  if (maxPct === -1) {
    return `Lectura grupal: segmentando por ${demographicName}, no se identifica un patrón concentrado que sugiera valoración clínica prioritaria en algún subgrupo. Mantenga protocolos de atención y documentación conforme a su política interna.`;
  }
  return `Lectura grupal (patrones agregados): en ${demographicName}, el subgrupo "${maxKey}" presenta la mayor proporción relativa de participantes con criterios de valoración clínica según el cuestionario (${(maxPct * 100).toFixed(1)}% dentro de ese subgrupo). No constituye diagnóstico individual; orienta a RH y dirección a reforzar canales confidenciales, acompañamiento y, cuando corresponda, derivación conforme a protocolo. Conviene complementar con entrevistas o grupos focales respetuosos y evitar estigmatizar al subgrupo.`;
}

export function buildNom35ExecutiveSummaryBlocks(data: Nom35ExecutiveGlobalResult): {
  globalRiskLine: string;
  criticalDomainsLine: string;
  demographicLine: string;
  executiveReading: string;
  priorityRecommendation: string;
} {
  const g2 = data.g2g3;
  const g1 = data.g1;

  if (g2) {
    const globalRisk = g2.globalRiskLevel;
    const mapped = buildOfficialCategoryRows(g2);
    const criticalSections = mapped
      .filter(({ rows }) => isElevated(topRiskFromDomains(rows)))
      .map(({ category, rows }) => {
        const hot = rows.filter((r) => isElevated(r.risk)).map((r) => r.name);
        const domPart = hot.length ? ` (${hot.join(', ')})` : '';
        return `${category.title.replace('Categoría: ', '')}${domPart}`;
      });

    const focos: string[] = [];
    for (const [label, table, name] of [
      ['sexo', g2.crosses.riskBySex, 'sexo'],
      ['rango de edad', g2.crosses.riskByAgeRange, 'edad'],
      ['departamento', g2.crosses.riskByDepartment, 'departamento'],
    ] as const) {
      let maxPct = -1;
      let maxKey = '';
      for (const [key, counts] of Object.entries(table)) {
        if (counts.total === 0 || key === 'No especificado') continue;
        const high = counts.alto + counts.muyAlto;
        const pct = high / counts.total;
        if (pct > maxPct && high > 0) {
          maxPct = pct;
          maxKey = key;
        }
      }
      if (maxPct >= 0 && maxKey) {
        focos.push(`${name} (${maxKey}: ${(maxPct * 100).toFixed(1)}% en Alto/Muy Alto dentro del subgrupo)`);
      }
    }

    const criticalDomainsLine =
      criticalSections.length > 0
        ? `Dominios y categorías que conviene revisar con prioridad: ${criticalSections.join('; ')}.`
        : 'A nivel de categorías del cuestionario, no se detectaron dominios en riesgo elevado según la lectura relativa de este informe; mantenga vigilancia en las áreas de mayor variabilidad.';

    const demographicLine =
      focos.length > 0
        ? `Concentraciones relativas de riesgo más altas (siempre como tendencias de grupo): ${focos.join('; ')}.`
        : 'Los cruces demográficos no muestran una concentración singular destacable en Alto o Muy Alto; aun así, se recomienda escucha cualitativa periódica.';

    const executiveReading = `El nivel global de riesgo psicosocial (${globalRisk}) describe cómo se posiciona el centro frente a la exposición promedio reportada en esta medición. ${criticalSections.length ? 'Si varias categorías muestran lectura elevada, conviene revisar de forma integrada clima, coordinación y distribución de cargas, sin asumir una sola causa.' : 'Los resultados globales se alinean con un panorama relativamente acotado; conviene preservar factores protectores y atender con prontitud cualquier señal operativa puntual.'} La lectura puede complementarse con indicadores de ausentismo, rotación y clima que la organización ya utilice; este informe no establece por sí solo relaciones causales.`;

    const priorityRecommendation =
      globalRisk === 'Muy Alto' || globalRisk === 'Alto'
        ? 'Recomendación prioritaria: constituir un comité de seguimiento breve (RH, líderes y SST), acordar intervenciones focalizadas por categoría y departamento, y documentar plazos y responsables en un plan de trabajo alineado a la NOM-035.'
        : globalRisk === 'Medio'
          ? 'Recomendación prioritaria: planificar intervenciones preventivas focalizadas, comunicar canales de apoyo y calendarizar una revisión a 90 días con indicadores de seguimiento acordados.'
          : 'Recomendación prioritaria: sostener acciones preventivas, comunicación sobre bienestar y revisión periódica de factores de riesgo, incorporando retroalimentación de equipos.';

    return {
      globalRiskLine: `Nivel global de riesgo (promedio del centro, según guía aplicada): ${globalRisk}.`,
      criticalDomainsLine,
      demographicLine,
      executiveReading,
      priorityRecommendation,
    };
  }

  if (g1) {
    const pct = g1.totalEvaluated > 0 ? (g1.requiresAttentionCount / g1.totalEvaluated) * 100 : 0;
    return {
      globalRiskLine:
        'Esta campaña incluye Guía I (acontecimientos traumáticos severos). No aplica el mismo indicador de riesgo global que la Guía II/III; la lectura se centra en la proporción de participantes que requieren valoración clínica según criterios del cuestionario.',
      criticalDomainsLine: `Proporción que requiere valoración clínica según criterios de la Guía I: ${pct.toFixed(1)}% (${g1.requiresAttentionCount} de ${g1.totalEvaluated}).`,
      demographicLine: 'Revise los cruces por sexo, edad y departamento en el apartado correspondiente: describen patrones agregados y orientan conversaciones de apoyo, no conclusiones sobre personas.',
      executiveReading:
        'Los resultados de la Guía I orientan a reforzar protocolos de atención, confidencialidad y derivación profesional cuando corresponda. Conviene integrar esta lectura con el acompañamiento psicosocial que la empresa ya disponga.',
      priorityRecommendation:
        'Recomendación prioritaria: verificar que existan canales claros de atención, comunicación sensible a equipos y registro documental de seguimiento conforme a política interna y marco normativo aplicable.',
    };
  }

  return {
    globalRiskLine: 'No hay datos suficientes en esta exportación para sintetizar el riesgo.',
    criticalDomainsLine: '—',
    demographicLine: '—',
    executiveReading: 'Conviene validar la campaña de evaluación y repetir el informe cuando existan respuestas completas.',
    priorityRecommendation: 'Recomendación prioritaria: completar la evaluación y asegurar participación representativa antes de decisiones organizacionales.',
  };
}

function categoryNarrativeKey(category: CategorySection): 'ambiente' | 'factores' | 'tiempo' | 'liderazgo' | 'entorno' {
  const t = category.title;
  if (t.includes('Ambiente de Trabajo')) return 'ambiente';
  if (t.includes('Factores propios')) return 'factores';
  if (t.includes('Organización del tiempo')) return 'tiempo';
  if (t.includes('Liderazgo y relaciones')) return 'liderazgo';
  return 'entorno';
}

export function expandedCategoryNarrative(category: CategorySection, rows: DomainRow[]): string {
  const top = topRiskFromDomains(rows);
  const intro = category.intro;
  const k = categoryNarrativeKey(category);

  const head = `Qué abarca esta categoría: ${intro}`;

  type NarrativeTier = 'Alto' | 'Medio' | 'Bajo';
  const dynamicsByKey: Record<
    'ambiente' | 'factores' | 'tiempo' | 'liderazgo' | 'entorno',
    Record<NarrativeTier, string>
  > = {
    ambiente: {
      Alto: 'Con lecturas altas o muy altas, cabe revisar si las condiciones materiales o de seguridad percibidas generan tensión o limitan el desempeño seguro. La lectura es agregada y orienta a priorizar inspección conjunta con SST y líderes de área.',
      Medio: 'Un perfil medio invita a revisar puntos concretos del entorno físico (ruido, iluminación, orden, ergonomía) antes de que se acumulen molestias o incidentes.',
      Bajo: 'Los niveles bajos o nulos suelen indicar menor presión reportada en condiciones de ambiente; conviene mantener estándares y revisiones periódicas.',
    },
    factores: {
      Alto: 'Con riesgo elevado aquí, suele aparecer la percepción de ritmo intenso, cargas desproporcionadas o poca autonomía para organizar el trabajo. Conviene revisar distribución de tareas, claridad de prioridades y márgenes de maniobra real, sin culpabilizar a equipos.',
      Medio: 'Un perfil medio sugiere revisar cargas, plazos y grado de control sobre la forma de ejecutar las tareas; pequeños ajustes suelen aliviar presión antes de que escale.',
      Bajo: 'Los niveles controlables en esta categoría son un buen punto de partida; mantenga claridad de roles y canales para reportar sobrecargas puntuales.',
    },
    tiempo: {
      Alto: 'Con riesgo elevado, es frecuente que emerjan fatiga por jornada, dificultad para desconectar o tensión entre trabajo y vida personal. Conviene revisar políticas de tiempo, descansos, predictibilidad de horarios y prácticas de desconexión, siempre con enfoque preventivo.',
      Medio: 'Un perfil medio invita a revisar extensiones puntuales de jornada, disponibilidad fuera de horario y apoyo cuando hay picos de demanda.',
      Bajo: 'Los niveles bajos o nulos suelen reflejar mejor encaje entre jornada y descanso percibido; preserve buenas prácticas y monitoree cambios de temporada o de proyecto.',
    },
    liderazgo: {
      Alto: 'Con riesgo elevado, las personas pueden percibir vacíos en comunicación, apoyo del mando inmediato, manejo del conflicto o prevención de conductas nocivas. Priorice diálogo con líderes, normas claras de respeto y protocolos de atención confidencial.',
      Medio: 'Un perfil medio sugiere reforzar retroalimentación, coherencia en decisiones y clima relacional antes de que la fricción se generalice.',
      Bajo: 'Los niveles controlables aquí favorecen la coordinación; sostenga liderazgo visible y canales de confianza.',
    },
    entorno: {
      Alto: 'Con riesgo elevado, puede percibirse poco reconocimiento, debilidad del sentido de pertenencia o incertidumbre ante cambios. Conviene trabajar comunicación sobre rumbo, valoración del esfuerzo y estabilidad razonable en reglas del juego.',
      Medio: 'Un perfil medio invita a reforzar reconocimiento justo, participación en decisiones que afectan al equipo y transparencia sobre cambios relevantes.',
      Bajo: 'Los niveles bajos o nulos suelen asociarse a mayor percepción de valoración e identificación; mantenga rutinas de feedback y oportunidades de desarrollo.',
    },
  };

  const tier: NarrativeTier = top === 'Muy Alto' || top === 'Alto' ? 'Alto' : top === 'Medio' ? 'Medio' : 'Bajo';
  const dynamics = dynamicsByKey[k][tier];

  let implications = '';
  if (isElevated(top)) {
    implications =
      k === 'ambiente'
        ? 'En la práctica organizacional, ello puede relacionarse con incidentes, incomodidad prolongada o menor confianza en las condiciones de trabajo; conviene contrastar con inspecciones y datos de seguridad.'
        : k === 'factores'
          ? 'Puede asociarse con fatiga, errores o tensiones de desempeño; la relación con ausentismo o rotación no es automática y conviene validarla con otros indicadores.'
          : k === 'tiempo'
            ? 'Puede incidir en bienestar y sostenibilidad del ritmo de trabajo; integre la lectura con políticas de tiempo y flexibilidad ya existentes.'
            : k === 'liderazgo'
              ? 'Suele impactar en clima y colaboración; complemente con escucha cualitativa y revisión de prácticas de mando intermedio.'
              : 'Puede influir en motivación y permanencia; evite lecturas simplistas y aborde el tema con sensibilidad y plan concreto.';
  } else {
    implications =
      'Las implicaciones para clima y desempeño suelen ser más acotadas cuando el riesgo se mantiene bajo o moderado; el seguimiento periódico sigue siendo útil.';
  }

  let actions = '';
  if (top === 'Muy Alto' || top === 'Alto') {
    actions =
      k === 'ambiente'
        ? 'Acciones sugeridas: recorrido con SST, registro de acciones correctivas en condiciones físicas, capacitación breve y seguimiento a 30 días.'
        : k === 'factores'
          ? 'Acciones sugeridas: revisión de cargas con líderes, priorización explícita de entregables, ajuste de autonomía operativa y seguimiento acordado.'
          : k === 'tiempo'
            ? 'Acciones sugeridas: acuerdos de disponibilidad, reglas de desconexión, revisión de turnos o picos y comunicación clara ante cambios de jornada.'
            : k === 'liderazgo'
              ? 'Acciones sugeridas: taller breve para mandos medios, mediación preventiva, refuerzo del canal de confianza y seguimiento con indicadores de clima.'
              : 'Acciones sugeridas: plan de reconocimiento, mensajes sobre rumbo y estabilidad, espacios de escucha y revisión a 60–90 días.';
  } else if (top === 'Medio') {
    actions =
      'Acciones sugeridas: medidas preventivas focalizadas, ajustes puntuales de proceso y revisión en 30–60 días con responsable asignado.';
  } else {
    actions = 'Acciones sugeridas: mantener difusión de políticas, canales de confianza y revisiones programadas.';
  }

  return `${head} ${dynamics} ${implications} ${actions}`;
}

export function domainNarrativeParagraph(domainName: string, risk: Nom35RiskLevel): string | null {
  if (!isElevated(risk)) return null;

  const tone =
    risk === 'Muy Alto'
      ? 'Muy Alto'
      : risk === 'Alto'
        ? 'Alto'
        : 'Medio';

  const templates: Record<string, string> = {
    'Condiciones en el ambiente de trabajo': `Dominio "${domainName}" (${tone}): podría indicar percepción de riesgos físicos, ruido, iluminación, ergonomía o condiciones que dificultan el desempeño seguro. Conviene revisar condiciones materiales, capacitación en seguridad y participación de brigadas, sin asumir negligencia a priori.`,
    'Carga de trabajo': `Dominio "${domainName}" (${tone}): sugiere que los equipos podrían estar experimentando volumen, ritmo o plazos exigentes. Podría relacionarse con fatiga, errores o tensiones; conviene analizar distribución de tareas, priorización y apoyo logístico.`,
    'Falta de control sobre el trabajo': `Dominio "${domainName}" (${tone}): las personas podrían percibir poca influencia sobre cómo organizan su trabajo o sobre decisiones que les afectan. Conviene explorar criterios más claros, autonomía gradual y transparencia en decisiones.`,
    Responsabilidades: `Dominio "${domainName}" (${tone}): podría reflejar ambigüedad de roles, responsabilidades superpuestas o expectativas poco alineadas. Se sugiere clarificar mandatos y coordinación entre áreas.`,
    'Jornada de trabajo': `Dominio "${domainName}" (${tone}): podría indicar fatiga vinculada a extensiones de jornada, discontinuidad de descansos o dificultad para desconectar. Revise políticas de tiempo, rotaciones y uso de herramientas digitales fuera de horario.`,
    'Interferencia en la relación trabajo-familia': `Dominio "${domainName}" (${tone}): sugiere tensión entre demandas laborales y vida personal. Conviene revisar flexibilidad real, predictibilidad de horarios y apoyo a situaciones familiares, sin invadir la esfera privada.`,
    Liderazgo: `Dominio "${domainName}" (${tone}): los colaboradores podrían percibir deficiencias en instrucciones, retroalimentación, apoyo del mando inmediato o consistencia en decisiones. Conviene tratarlo como prioridad de intervención organizacional, sobre todo si coexisten quejas de conflicto, baja motivación o rotación.`,
    'Relaciones en el trabajo': `Dominio "${domainName}" (${tone}): podría indicar fricción, aislamiento o comunicación deficiente entre pares o áreas. Se recomienda facilitar espacios de diálogo, mediación preventiva y normas claras de respeto.`,
    Violencia: `Dominio "${domainName}" (${tone}): cualquier señal elevada merece atención organizacional seria y confidencial. Conviene reforzar protocolos, canal de denuncia y acompañamiento, sin revictimizar y priorizando la seguridad psicológica.`,
    'Reconocimiento del desempeño': `Dominio "${domainName}" (${tone}): sugiere insatisfacción con la retroalimentación o visibilidad del esfuerzo. Revise prácticas de reconocimiento justo, comunicación de logros y coherencia con criterios de evaluación.`,
    'Insuficiente sentido de pertenencia e inestabilidad': `Dominio "${domainName}" (${tone}): podría asociarse a incertidumbre ante cambios, poca identificación con la organización o percepción de inestabilidad. Conviene mejorar comunicación sobre rumbo, oportunidades de desarrollo y transparencia razonable.`,
  };

  return templates[domainName] ?? `Dominio "${domainName}" (${tone}): conviene explorar con líderes y equipos las causas organizacionales posibles y definir acciones focalizadas, complementando con escucha cualitativa.`;
}

export function nom35KeyFindingsAndPhasedPlan(data: Nom35ExecutiveGlobalResult): {
  findings: string;
  urgency: string;
  phaseImmediate: string;
  phaseShort: string;
  phaseFollowUp: string;
  responsibilityNote: string;
} {
  const globalRisk = data.g2g3?.globalRiskLevel ?? 'Nulo';
  const urgency = `Nivel de urgencia: ${riskUrgencyLabel(globalRisk)}`;

  let findings = '';
  if (data.g2g3) {
    const mapped = buildOfficialCategoryRows(data.g2g3);
    const elevated = mapped.filter(({ rows }) => isElevated(topRiskFromDomains(rows)));
    findings = `Hallazgos clave: riesgo global ${globalRisk}. `;
    findings +=
      elevated.length > 0
        ? `Se identifican categorías con lectura elevada en al menos un dominio: ${elevated.map((e) => e.category.title.replace('Categoría: ', '')).join(', ')}. `
        : 'Las categorías revisadas se mantienen en rangos relativos bajos o controlables en esta medición. ';
    findings +=
      'Los cruces demográficos describen tendencias agregadas; integre esta información con otras fuentes antes de decisiones de personal o estructura.';
  } else if (data.g1) {
    findings = `Hallazgos clave (Guía I): ${data.g1.requiresAttentionCount} participantes con criterios de valoración clínica de un total de ${data.g1.totalEvaluated}. Los cruces demográficos son agregados y orientan políticas de apoyo, no juicios sobre individuos.`;
  } else {
    findings = 'Hallazgos clave: información insuficiente para síntesis cuantitativa en esta exportación.';
  }

  const phaseImmediate =
    'Fase 0-30 días: comunicar a dirección y líderes los resultados agregados; activar comité de seguimiento; priorizar escucha cualitativa en áreas señaladas; revisar recursos de apoyo psicosocial y canales confidenciales; acordar tres acciones concretas con responsable y fecha.';

  const phaseShort =
    'Fase 1-3 meses: implementar ajustes organizacionales acordados (cargas, claridad de roles, liderazgo), talleres breves, seguimiento a indicadores de clima y ausentismo que la empresa ya utilice; documentar avances en actas o sistema de gestión.';

  const phaseFollowUp =
    'Fase 3-6 meses: reevaluación o contraste con nueva medición según calendario NOM-035; análisis de efectividad de intervenciones; ajuste del plan preventivo con base en evidencia mixta (cuantitativa y cualitativa).';

  const responsibilityNote =
    'Nota de responsabilidad: este informe orienta la toma de decisiones preventivas y de mejora organizacional. La empresa debe documentar la implementación, asignación de recursos y seguimiento de acciones. No sustituye valoración clínica individual ni garantiza por sí mismo el cumplimiento de obligaciones legales: corresponde al centro de trabajo integrar el informe en su sistema de gestión y asesoría especializada que corresponda.';

  return {
    findings,
    urgency,
    phaseImmediate,
    phaseShort,
    phaseFollowUp,
    responsibilityNote,
  };
}
